import React from 'react';

/**
 * Production-safe top-level error boundary.
 *
 * Catches uncaught React render/startup errors so users see a friendly fallback
 * instead of a blank white screen (the TestFlight failure mode we're guarding
 * against). Logs error + componentStack to console.error so Safari Web Inspector
 * or Xcode Console picks them up.
 *
 * Dev/non-production builds: shows a short technical excerpt to aid debugging.
 * Production/TestFlight: shows only the friendly message; no secrets exposed.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Structured record — visible in Safari Web Inspector / Xcode Console
    // on device.  Never includes tokens, passwords, or user data.
    const platform = (typeof window !== 'undefined' && window.Capacitor?.getPlatform?.()) || 'web';
    const route = (typeof window !== 'undefined' && window.location?.pathname) || 'unknown';
    console.error('[ZenScroll] Top-level error boundary caught:', {
      route,
      platform,
      build: process.env.REACT_APP_BUILD_ID || process.env.NODE_ENV,
      name: error?.name,
      message: error?.message,
      stack: (error?.stack || '').split('\n').slice(0, 5).join(' | '),
      componentStack: (info?.componentStack || '').split('\n').slice(0, 8).join(' | '),
    });
    if (process.env.NODE_ENV !== 'production') {
      // Dev only: complete error objects for debugging.
      console.error('[ZenScroll] full error:', error, info);
    }
    this.setState({ info });
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_) {
      /* no-op */
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isDev = process.env.NODE_ENV !== 'production';
    const message = (this.state.error && this.state.error.message) || 'Unknown error';

    return (
      <div
        data-testid="app-error-fallback"
        style={{
          minHeight: '100vh',
          backgroundColor: '#0A1514',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily:
            "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div
            style={{
              fontSize: 48,
              marginBottom: 12,
              opacity: 0.9,
            }}
            aria-hidden="true"
          >
            ⚠️
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 600,
              marginBottom: 10,
              letterSpacing: '-0.01em',
            }}
          >
            ZenScroll had trouble loading
          </h1>
          <p style={{ fontSize: 14, color: '#94A3B8', lineHeight: 1.5, marginBottom: 24 }}>
            Please close and reopen the app. If this keeps happening, check your
            internet connection or update to the latest version.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="app-error-reload-btn"
            style={{
              background: '#1C4749',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 0 24px rgba(28,71,73,0.35)',
            }}
          >
            Try again
          </button>

          {isDev && (
            <pre
              data-testid="app-error-dev-detail"
              style={{
                marginTop: 24,
                padding: 12,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8,
                color: '#FCA5A5',
                fontSize: 11,
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 240,
                overflow: 'auto',
              }}
            >
              {String(message).slice(0, 600)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
